/**
 * useSignalRNotifications — React hook for SignalR notification lifecycle
 *
 * Matches Xamarin SignalRNofiticationService + SendNotificationUpdate:
 * 1. After plant selection, negotiate with server to get hub URL + token
 * 2. Subscribe to "Plant{orgId}" channel
 * 3. On notification: track unread task IDs, invalidate query caches, dispatch event for toast
 * 4. On plant change: unsubscribe old, re-subscribe new
 * 5. On unmount/logout: unsubscribe
 */

import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';

import { dashboardQueryKeys } from '@/hooks/dashboard';
import { negotiate, subscribe, unsubscribe } from '@/service/signalRService';
import {
  useAppSelector,
  selectAccessToken,
  selectPlant,
  selectFunctionCodes,
  selectSettings,
} from '@/store';
import { addUnreadNotificationId } from '@/utils';

import type { SignalRNotificationPayloadDTO } from '@/types';

/** Custom event name for SignalR push notifications (consumed by MainLayout toast) */
export const SIGNALR_NOTIFICATION_EVENT = 'signalr:notification';

export function useSignalRNotifications(): void {
  const queryClient = useQueryClient();

  const accessToken = useAppSelector(selectAccessToken);
  const plant = useAppSelector(selectPlant);
  const functionCodes = useAppSelector(selectFunctionCodes);
  const settings = useAppSelector(selectSettings);

  const orgId = plant?.org_id;
  const orgIdStr = String(orgId ?? '');
  const negotiateFunctionCode = functionCodes?.negotiate ?? '';
  const showDialog = settings?.SHOW_SIGNALR_NOTIFICATION_DIALOG ?? false;

  // Refs to avoid stale closures in callbacks
  const showDialogRef = useRef(showDialog);
  showDialogRef.current = showDialog;
  const orgIdStrRef = useRef(orgIdStr);
  orgIdStrRef.current = orgIdStr;

  // Matches Xamarin SendNotificationUpdate + NotificationUpdateEvent
  const handleNotification = useCallback(
    (payload: SignalRNotificationPayloadDTO) => {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.log('[SignalR] handleNotification fired:', {
          type: payload.notifications_type,
          alert: payload.alert_type,
          task_id: payload.data.task_id,
          task_type: payload.data.task_type,
          orgId: orgIdStrRef.current,
        });
      }

      // Matches Xamarin SendNotificationUpdate: add task ID to UnreadNotificationTaskIdList
      // and increment bell badge count only for genuinely new task IDs (deduplicate)
      if (payload.data.task_id && payload.data.task_type !== 'Audit') {
        const isNew = addUnreadNotificationId(payload.data.task_id);

        if (isNew) {
          queryClient.setQueryData<number>(
            dashboardQueryKeys.notificationCount(orgIdStrRef.current),
            (old) => (old ?? 0) + 1
          );
        }
      }
      void queryClient.invalidateQueries({
        queryKey: ['dashboard', 'notificationList'],
      });

      // Matches Xamarin: show toast dialog if SHOW_SIGNALR_NOTIFICATION_DIALOG is enabled
      // and alert_type is not "silent"
      if (showDialogRef.current && payload.alert_type !== 'silent') {
        window.dispatchEvent(new CustomEvent(SIGNALR_NOTIFICATION_EVENT, { detail: payload }));
      }
    },
    [queryClient]
  );

  // Reconnected callback — refetch data
  const handleReconnected = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: dashboardQueryKeys.notificationCount(orgIdStrRef.current),
    });
  }, [queryClient]);

  // Main effect: connect/disconnect SignalR based on auth + plant state
  useEffect(() => {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log('[SignalR] Hook state:', {
        hasToken: !!accessToken,
        orgId,
        negotiateFunctionCode: negotiateFunctionCode || '(empty)',
      });
    }

    if (!accessToken || !orgId || !negotiateFunctionCode) return;

    let cancelled = false;

    const connect = async (attempt = 0) => {
      const MAX_RETRIES = 3;
      const RETRY_DELAY = 3000;

      try {
        const negotiateResponse = await negotiate(negotiateFunctionCode, accessToken);

        if (cancelled) return;

        if (!negotiateResponse.status || !negotiateResponse.url) {
          if (import.meta.env.DEV) {
            console.warn('[SignalR] Negotiate returned unsuccessful response');
          }
          return;
        }

        await subscribe(negotiateResponse.url, negotiateResponse.accessToken, orgId, {
          onNotification: handleNotification,
          onReconnected: handleReconnected,
        });
      } catch (error: unknown) {
        if (cancelled) return;

        // Retry on failure (e.g., 401 from stale token on page reload — token refresh
        // will update accessToken which re-triggers this effect, but retry handles the
        // window between stale token and refresh completing)
        if (attempt < MAX_RETRIES) {
          if (import.meta.env.DEV) {
            const msg = error instanceof Error ? error.message : 'Unknown error';
            console.warn(
              `[SignalR] Attempt ${String(attempt + 1)} failed: ${msg}, retrying in ${String(RETRY_DELAY / 1000)}s...`
            );
          }
          await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- cancelled can change during await
          if (!cancelled) {
            await connect(attempt + 1);
          }
        } else if (import.meta.env.DEV) {
          const msg = error instanceof Error ? error.message : 'Unknown error';
          console.warn('[SignalR] All retries failed, falling back to REST polling:', msg);
        }
      }
    };

    void connect();

    return () => {
      cancelled = true;
      void unsubscribe();
    };
  }, [accessToken, orgId, negotiateFunctionCode, handleNotification, handleReconnected]);

  // Online/offline awareness — invalidate caches when coming back online
  useEffect(() => {
    if (!accessToken || !orgId || !negotiateFunctionCode) return;

    const handleOnline = () => {
      void queryClient.invalidateQueries({
        queryKey: dashboardQueryKeys.notificationCount(orgIdStrRef.current),
      });
    };

    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, [accessToken, orgId, negotiateFunctionCode, queryClient]);
}
